import {
    Card,
    CardContent,
    Stack,
    Typography,
    Button,
    Box,
    Divider,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Snackbar,
} from '@mui/material';
import { Reply, Delete, Close, IosShare, WarningAmber } from '@mui/icons-material';
import { motion } from 'framer-motion';
import SlideUpTransition from '../components/SlideUpTransition';
import { Thread, ThreadMediaType } from '../database/forum';
import UserHeader from '../components/UserHeader';
import { useTheme } from '@mui/material';
import { getConfig } from '../config';
import ThreadMedia from './ThreadMedia';
import LikeButton from '../components/LikeButton';
import { LikeParentType } from '../theSource/constants/LikeParentType';
import { useAuth } from '../auth/Auth';
import { useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { RequestSnackbar, useRequest } from '../api/Request';
import { useApi } from '../api/Api';
import MentionText from '../components/MentionText';

interface ThreadCardProps {
    forumSk: string;
    thread: Thread;
    onSave: (thread: Thread) => void;
    onReply: (thread: Thread) => void;
    canReply: boolean;
    onDelete: (thread: Thread) => void;
    /** When true, thread content (text, media, interactions) is blurred */
    locked?: boolean;
    highlighted?: boolean;
}

const ThreadCard: React.FC<ThreadCardProps> = ({
    forumSk,
    thread,
    onSave,
    onReply,
    canReply,
    onDelete,
    locked = false,
    highlighted = false,
}) => {
    const theme = useTheme();
    const forumMediaBucket = getConfig().media.forumMediaBucket;
    const auth = useAuth();
    const user = auth.user;
    const api = useApi();
    const deleteThreadRequest = useRequest();
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showCopiedSnackbar, setShowCopiedSnackbar] = useState(false);

    const handleShare = async () => {
        const url = new URL(window.location.origin + '/ess');
        url.searchParams.set('topic', `${forumSk}~${thread.pk}`);
        url.searchParams.set('thread', `${thread.pk}~${thread.sk}`);
        const shareUrl = url.toString();

        if (navigator.share) {
            try {
                await navigator.share({
                    title: thread.contentText?.substring(0, 60) || 'Thread',
                    url: shareUrl,
                });
                return;
            } catch (err) {
                if ((err as DOMException).name === 'AbortError') return;
            }
        }

        try {
            await navigator.clipboard.writeText(shareUrl);
            setShowCopiedSnackbar(true);
        } catch {
            console.error('Failed to copy to clipboard');
        }
    };

    const handleDelete = (thread: Thread) => {
        deleteThreadRequest.onStart();
        api.deleteThread(forumSk, thread.pk, thread.sk, thread.username)
            .then((response) => {
                onDelete(response.data);
                deleteThreadRequest.onSuccess('Successfully deleted thread');
            })
            .catch((err) => {
                console.error('Delete community thread: ', err);
                deleteThreadRequest.onFailure(err);
            });
    };

    const getMediaUrl = (
        forumId: string,
        topicId: string,
        threadId: string,
        username: string,
        mediaType?: ThreadMediaType,
    ) => {
        if (!mediaType) return undefined;
        const encodedForumSk = encodeURIComponent(forumId);
        const encodedPk = encodeURIComponent(topicId);
        const encodedSk = encodeURIComponent(threadId);
        const encodedUsername = encodeURIComponent(username);

        return `${forumMediaBucket}/${encodedForumSk}/${encodedPk}/${encodedSk}/${encodedUsername}`;
    };

    const renderMedia = () => {
        const mediaUrl = getMediaUrl(
            forumSk,
            thread.pk,
            thread.sk,
            thread.username,
            thread.mediaType,
        );
        if (!mediaUrl || !thread.mediaType) return null;

        return <ThreadMedia mediaUrl={mediaUrl} mediaType={thread.mediaType} />;
    };

    const renderQuotedThread = () => {
        if (!thread.quotedThread) return null;
        const quotedMediaUrl = thread.quotedThread.mediaType
            ? getMediaUrl(
                  forumSk,
                  thread.pk,
                  thread.quotedThread.sk,
                  thread.quotedThread.username,
                  thread.quotedThread.mediaType,
              )
            : undefined;

        return (
            <Box
                sx={{
                    mt: 2,
                    mb: 2,
                    p: 2,
                    borderLeft: `3px solid ${theme.palette.primary.main}`,
                    bgcolor: `${theme.palette.primary.main}15`,
                    borderRadius: 1,
                }}
            >
                <Stack spacing={1}>
                    <Typography variant='caption'>Replying to:</Typography>
                    <UserHeader
                        username={thread.quotedThread.username}
                        displayName={thread.quotedThread.displayName}
                        isArtist={thread.quotedThread.isArtist}
                        subscriptionStatus={thread.quotedThread.subscriptionStatus}
                        subscriptionOverride={thread.quotedThread.subscriptionOverride}
                        timestamp={thread.quotedThread.createdAt}
                        avatarSize={30}
                    />
                    {thread.quotedThread.contentText && (
                        <MentionText variant='body2' sx={{ opacity: 0.8 }}>
                            {thread.quotedThread.contentText}
                        </MentionText>
                    )}
                    {thread.quotedThread.mediaType && quotedMediaUrl && (
                        <ThreadMedia
                            mediaUrl={quotedMediaUrl}
                            mediaType={thread.quotedThread.mediaType}
                        />
                    )}
                </Stack>
            </Box>
        );
    };

    return (
        <>
            <Snackbar
                open={showCopiedSnackbar}
                autoHideDuration={2000}
                onClose={() => setShowCopiedSnackbar(false)}
                message='Link copied to clipboard'
            />
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.28, ease: 'easeOut' }}
            >
                <Card
                    elevation={2}
                    sx={{
                        borderLeft: `4px solid ${theme.palette.primary.main}`,
                        transition: 'all 0.2s',
                        ...(highlighted && {
                            boxShadow: `0 0 18px 7px ${theme.palette.primary.main}60`,
                            borderLeftWidth: '6px',
                        }),
                        '&:hover': {
                            transform: 'translateX(2px)',
                            boxShadow: `0px 4px 14px ${theme.palette.primary.main}40`,
                            borderLeftWidth: '6px',
                        },
                    }}
                >
                    <Dialog
                        open={showDeleteDialog}
                        onClose={
                            deleteThreadRequest.isLoading()
                                ? undefined
                                : () => setShowDeleteDialog(false)
                        }
                        TransitionComponent={SlideUpTransition}
                        PaperProps={{
                            sx: {
                                borderColor: 'rgba(211, 47, 47, 0.4)',
                                boxShadow: '0 24px 64px rgba(0,0,0,0.85), 0 0 0 1px rgba(211,47,47,0.2)',
                            },
                        }}
                    >
                        <RequestSnackbar request={deleteThreadRequest} />
                        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <WarningAmber sx={{ color: 'error.main', fontSize: '1.3rem' }} />
                            Delete this thread?
                            <IconButton
                                aria-label='close'
                                onClick={() => setShowDeleteDialog(false)}
                                sx={{ position: 'absolute', right: 10, top: 8 }}
                                disabled={deleteThreadRequest.isLoading()}
                            >
                                <Close />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText sx={{ color: theme.palette.secondary.main }}>
                                Are you sure you want to delete this thread? You can't undo
                                this action.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                color='secondary'
                                onClick={() => setShowDeleteDialog(false)}
                                disabled={deleteThreadRequest.isLoading()}
                            >
                                Cancel
                            </Button>
                            <LoadingButton
                                color='error'
                                variant='contained'
                                onClick={() => handleDelete(thread)}
                                loading={deleteThreadRequest.isLoading()}
                            >
                                Delete Thread
                            </LoadingButton>
                        </DialogActions>
                    </Dialog>
                    <CardContent sx={{ py: 2.5 }}>
                        <Stack spacing={2}>
                            <Stack direction='row' padding='10px' alignItems='center'>
                                <UserHeader
                                    username={thread.username}
                                    displayName={thread.displayName}
                                    isArtist={thread.isArtist}
                                    subscriptionStatus={thread.subscriptionStatus}
                                    subscriptionOverride={thread.subscriptionOverride}
                                    timestamp={thread.createdAt}
                                    avatarSize={40}
                                />
                                <Stack direction='row' sx={{ marginLeft: 'auto' }}>
                                    {user &&
                                        (user.isAdmin ||
                                            user.isArtist ||
                                            user.username === thread.username) && (
                                            <IconButton
                                                onClick={() => setShowDeleteDialog(true)}
                                                sx={{
                                                    color: `${theme.palette.primary.main}`,
                                                    transition: 'opacity 0.15s',
                                                    '&:hover': { opacity: 0.75 },
                                                }}
                                            >
                                                <Delete />
                                            </IconButton>
                                        )}
                                    <IconButton
                                        onClick={handleShare}
                                        sx={{
                                            color: `${theme.palette.primary.main}`,
                                            transition: 'opacity 0.15s',
                                            '&:hover': { opacity: 0.75 },
                                        }}
                                    >
                                        <IosShare fontSize='small' />
                                    </IconButton>
                                </Stack>
                            </Stack>

                            {/* Blurred content section for locked threads */}
                            <Box
                                sx={{
                                    ...(locked && {
                                        filter: 'blur(6px)',
                                        pointerEvents: 'none',
                                        userSelect: 'none',
                                        WebkitUserSelect: 'none',
                                    }),
                                }}
                            >
                                {renderQuotedThread()}

                                {thread.contentText && (
                                    <MentionText
                                        variant='body1'
                                        sx={{ whiteSpace: 'pre-wrap' }}
                                    >
                                        {thread.contentText}
                                    </MentionText>
                                )}

                                {renderMedia()}
                            </Box>

                            <Divider sx={{ opacity: 0.3 }} />
                            {locked ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        py: 0.5,
                                        opacity: 0.6,
                                    }}
                                >
                                    <Typography variant='caption' color='text.secondary'>
                                        Subscribe to interact
                                    </Typography>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    <LikeButton<Thread>
                                        parentType={LikeParentType.Thread}
                                        pk={thread.pk}
                                        sk={thread.sk}
                                        likes={thread.likes}
                                        onEdit={onSave}
                                    />
                                    {canReply && (
                                        <motion.div
                                            whileTap={{ scale: 0.92 }}
                                            transition={{ duration: 0.1 }}
                                        >
                                            <Button
                                                startIcon={<Reply />}
                                                size='small'
                                                onClick={() => onReply(thread)}
                                            >
                                                Reply
                                            </Button>
                                        </motion.div>
                                    )}
                                </Box>
                            )}

                            {thread.quote && (
                                <>
                                    <Divider sx={{ opacity: 0.3 }} />
                                    <Box
                                        sx={{
                                            mt: 1,
                                            p: 1.5,
                                            bgcolor: theme.palette.grey[600],
                                            borderRadius: 1,
                                            ...(locked && {
                                                filter: 'blur(6px)',
                                                pointerEvents: 'none',
                                                userSelect: 'none',
                                            }),
                                        }}
                                    >
                                        <Typography
                                            variant='body2'
                                            sx={{
                                                fontStyle: 'italic',
                                                opacity: 0.8,
                                            }}
                                        >
                                            "{thread.quote}"
                                        </Typography>
                                    </Box>
                                </>
                            )}
                        </Stack>
                    </CardContent>
                </Card>
            </motion.div>
        </>
    );
};

export default ThreadCard;
